import moment from "moment";
import { bookingCapacity, capacityProblems } from "../../services/moksha-reservations/capacity";
import { DATE } from "../../services/moksha-reservations/format";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const round2 = (n) => Math.round(n * 100) / 100;

// Inputs hold strings; every count is parsed here. Blank or invalid is NaN.
export const countOf = (value) => (value === "" || value === null || value === undefined ? NaN : parseInt(value, 10));
const countOrZero = (value) => countOf(value) || 0;

export const BASES = {
    rack: {
        label: "Rack",
        field: "rackRate",
        param: "rack_rate",
        rateLabel: "Rack rate (stay total, excl. GST)",
        missing: "Enter the rack rate for the whole stay, excluding GST.",
    },
    contract: {
        label: "Contract",
        field: "contractRate",
        param: "contract_rate",
        rateLabel: "Contract rate (stay total, incl. GST)",
        missing: "Enter the contract rate for the whole stay, including GST.",
    },
};

// Per room, per night. The room rate must be > 0; extra bed and meal are optional, >= 0.
export const NIGHT_FIELDS = {
    rate: { values: "rates", same: "sameRate", param: "amount" },
    extraBed: { values: "extraBeds", same: "sameExtraBed", param: "extra_bed_price" },
    meal: { values: "meals", same: "sameMeal", param: "meal_plan_price" },
};
const KINDS = Object.keys(NIGHT_FIELDS);
const EXTRA_KINDS = ["extraBed", "meal"];

const emptyNightFields = () =>
    Object.fromEntries(KINDS.flatMap((k) => [[NIGHT_FIELDS[k].values, {}], [NIGHT_FIELDS[k].same, ""]]));

export const emptyDraft = () => ({
    checkin: "",
    checkout: "",
    lines: [],
    guest: { name: "", email: "", phone: "", address: "", remarks: "" },
    agentId: "",
    basis: "rack",
    rackRate: "",
    contractRate: "",
    noOfAdults: "",
    noOfChilds: "0",
});

let lineKey = 0;
export const newLine = (roomtype) => ({
    key: ++lineKey,
    roomtypeId: roomtype.roomtype_id,
    mealPlanId: roomtype.meal_plans?.[0]?.meal_plan_id || "",
    noOfRooms: "1",
    ...emptyNightFields(),
});

export const lineFieldId = (key, field) => `line-${key}-${field}`;
export const nightFieldId = (key, kind, date) => lineFieldId(key, `${kind}-${date}`);
export const basisRateId = (basis) => `basisRate-${basis}`;

const dateProblems = (checkin, checkout) => {
    const problems = {};
    if (checkin && moment(checkin, DATE).isBefore(moment().startOf("day")))
        problems.checkin = "Check-in can't be in the past. Pick today or a later date.";
    if (checkin && checkout && !moment(checkout, DATE).isAfter(moment(checkin, DATE), "day"))
        problems.checkout = "Check-out must be at least one day after check-in.";
    return problems;
};

const isStale = (draft, checkedDates) =>
    !!checkedDates && (checkedDates.checkin !== draft.checkin || checkedDates.checkout !== draft.checkout);

const nightValue = (line, kind, date) => round2(parseFloat(line[NIGHT_FIELDS[kind].values]?.[date]) || 0);

const lineSum = (line, nights, kinds) =>
    nights.reduce((sum, date) => sum + kinds.reduce((s, k) => s + nightValue(line, k, date), 0), 0) *
    countOrZero(line.noOfRooms);

export const lineTotal = (line, nights) => lineSum(line, nights, KINDS);
const lineExtras = (line, nights) => lineSum(line, nights, EXTRA_KINDS);

export const roomtypeOf = (availability, id) => availability?.roomtypes?.find((r) => r.roomtype_id === id);

export const addBlockedReason = (roomtype, lines) =>
    !(roomtype.available_rooms > 0)
        ? "Sold out"
        : lines.some((l) => l.roomtypeId === roomtype.roomtype_id)
        ? "Already added"
        : "";

export const withRooms = (line, raw, available) => {
    if (raw === "") return { ...line, noOfRooms: "" };
    return { ...line, noOfRooms: String(Math.max(0, Math.min(countOrZero(raw), available))) };
};

export const withSameRate = (line, kind, value, nights) => ({
    ...line,
    [NIGHT_FIELDS[kind].same]: value,
    [NIGHT_FIELDS[kind].values]: Object.fromEntries(nights.map((d) => [d, value])),
});

export const withNightRate = (line, kind, date, value) => {
    const key = NIGHT_FIELDS[kind].values;
    return { ...line, [key]: { ...line[key], [date]: value } };
};

// Rates are typed per night, so a different stay invalidates them.
export const withRatesCleared = (lines) => lines.map((l) => ({ ...l, ...emptyNightFields() }));

export const withConflicts = (availability, conflicts) => ({
    ...availability,
    roomtypes: availability.roomtypes.map((rt) => {
        const conflict = conflicts.find((c) => c.roomtype_id === rt.roomtype_id);
        return conflict ? { ...rt, available_rooms: conflict.available_rooms } : rt;
    }),
});

export const conflictFieldIds = (lines, conflicts) =>
    lines
        .filter((l) => conflicts.some((c) => c.roomtype_id === l.roomtypeId))
        .map((l) => lineFieldId(l.key, "rooms"));

const basisRateOf = (draft) => draft[BASES[draft.basis].field];

const lineErrors = (line, availability, nights) => {
    const e = {};
    const rt = roomtypeOf(availability, line.roomtypeId);
    const available = rt?.available_rooms ?? 0;
    const rooms = countOf(line.noOfRooms);
    const roomsId = lineFieldId(line.key, "rooms");
    if (!rt) e[roomsId] = "This roomtype isn't free for these dates. Remove it.";
    else if (!(rooms >= 1)) e[roomsId] = "Enter at least 1 room.";
    else if (rooms > available) e[roomsId] = `Only ${available} free. Lower the rooms.`;
    if (rt && !rt.meal_plans?.some((mp) => mp.meal_plan_id === line.mealPlanId))
        e[lineFieldId(line.key, "mealPlan")] = "Choose a rate plan.";
    nights.forEach((date) => {
        // Checked on the rounded value, which is what gets sent.
        if (!(nightValue(line, "rate", date) > 0)) e[nightFieldId(line.key, "rate", date)] = "Enter a rate above 0.";
        EXTRA_KINDS.forEach((kind) => {
            const raw = line[NIGHT_FIELDS[kind].values]?.[date];
            if (raw !== undefined && raw !== "" && !(parseFloat(raw) >= 0))
                e[nightFieldId(line.key, kind, date)] = "Enter 0 or more.";
        });
    });
    return e;
};

const capacityOf = (lines, availability) =>
    bookingCapacity(lines.map((l) => ({ roomtype: roomtypeOf(availability, l.roomtypeId), rooms: countOrZero(l.noOfRooms) })));

const guestCountErrors = (draft, capacity) => {
    const e = {};
    const adults = countOf(draft.noOfAdults);
    const childs = draft.noOfChilds === "" ? 0 : countOf(draft.noOfChilds);
    if (!(childs >= 0)) e.childs = "Children can't be negative. Enter 0 or more.";
    if (!(adults >= 1)) e.adults = "Enter the number of adults.";
    // No rooms yet: formProblem already asks for a room type, so 0-capacity limits would only mislead.
    if (e.adults || !capacity.rooms) return e;
    const problems = capacityProblems(adults, e.childs ? 0 : childs, capacity);
    if (problems.adults) e.adults = problems.adults;
    if (problems.children && !e.childs) e.childs = problems.children;
    return e;
};

const draftErrors = (draft, availability, nights, capacity) => {
    const e = Object.assign(guestCountErrors(draft, capacity), ...draft.lines.map((line) => lineErrors(line, availability, nights)));
    const { name, email } = draft.guest;
    if (!name.trim()) e.guestName = "Enter the guest's name.";
    if (!email.trim()) e.guestEmail = "Enter the guest's email.";
    else if (!EMAIL_PATTERN.test(email.trim())) e.guestEmail = "That email doesn't look right. Check it for a typo.";
    if (!(parseFloat(basisRateOf(draft)) > 0)) e[basisRateId(draft.basis)] = BASES[draft.basis].missing;
    return e;
};

export const deriveDraft = (draft, availability, checkedDates) => {
    const dateErrors = dateProblems(draft.checkin, draft.checkout);
    const datesValid = !!draft.checkin && !!draft.checkout && !dateErrors.checkin && !dateErrors.checkout;
    const nightCount = datesValid ? moment(draft.checkout, DATE).diff(moment(draft.checkin, DATE), "days") : 0;
    const stale = isStale(draft, checkedDates);
    const nights = availability?.nights || [];
    const specialRate = round2(draft.lines.reduce((sum, line) => sum + lineTotal(line, nights), 0));
    const extrasTotal = round2(draft.lines.reduce((sum, line) => sum + lineExtras(line, nights), 0));
    const capacity = capacityOf(draft.lines, availability);
    const errors = draftErrors(draft, availability, nights, capacity);
    const formProblem =
        dateErrors.checkin ||
        dateErrors.checkout ||
        (stale
            ? "The dates changed. Check availability again before creating the booking."
            : !draft.lines.length
            ? "Add at least one roomtype from the availability list."
            : !(specialRate > 0)
            ? "Enter the nightly rates so the special rate is above 0."
            : "");

    return {
        dateErrors,
        datesValid,
        nightCount,
        stale,
        nights,
        specialRate,
        extrasTotal,
        capacity,
        totalGuests: countOrZero(draft.noOfAdults) + countOrZero(draft.noOfChilds),
        basisRate: basisRateOf(draft),
        errors,
        errorCount: Object.keys(errors).length,
        formProblem,
    };
};

// Every field on screen; a failed submit marks these touched, fields added later stay clean.
export const fieldIds = (draft, nights) => [
    "guestName",
    "guestEmail",
    "adults",
    "childs",
    basisRateId(draft.basis),
    ...draft.lines.flatMap((l) => [
        lineFieldId(l.key, "mealPlan"),
        lineFieldId(l.key, "rooms"),
        ...nights.flatMap((d) => KINDS.map((k) => nightFieldId(l.key, k, d))),
    ]),
];

const optional = (value) => (value && value.trim() ? value.trim() : undefined);

export const toRequestBody = (draft, nights) => ({
    checkin_date: draft.checkin,
    checkout_date: draft.checkout,
    guest_name: draft.guest.name.trim(),
    guest_email: draft.guest.email.trim(),
    guest_phone: optional(draft.guest.phone),
    guest_address: optional(draft.guest.address),
    agent_id: draft.agentId || undefined,
    remarks: optional(draft.guest.remarks),
    commission_basis: draft.basis,
    [BASES[draft.basis].param]: parseFloat(basisRateOf(draft)),
    no_of_adults: countOf(draft.noOfAdults),
    no_of_childs: countOrZero(draft.noOfChilds),
    lines: draft.lines.map((line) => ({
        roomtype_id: line.roomtypeId,
        meal_plan_id: line.mealPlanId,
        no_of_rooms: countOf(line.noOfRooms),
        day_wise_rates: nights.map((date) => ({
            date,
            ...Object.fromEntries(KINDS.map((k) => [NIGHT_FIELDS[k].param, nightValue(line, k, date)])),
        })),
    })),
});
